// Projected GA encoding. The DB column stores a single TEXT value that
// discriminates on prefix:
//
//   null              → not set (no plan)
//   "TBD"             → planning, no date yet
//   "Q:<label>"       → quarter (e.g. "Q:Q1 2026")
//   "W:<iso-week>"    → ISO week (e.g. "W:2026-W45" — Monday of that week)
//   ISO datetime      → specific date (e.g. "2026-08-15T00:00:00")
//
// Kept as a single column so the schema stays flat; parse/format helpers
// below give the client a discriminated union to work with.

export type ProjectedGaKind = "unset" | "tbd" | "quarter" | "week" | "date";

export type ProjectedGa =
  | { kind: "unset" }
  | { kind: "tbd" }
  | { kind: "quarter"; value: string }
  | { kind: "week"; value: string } // "YYYY-Www"
  | { kind: "date"; value: string }; // ISO datetime string

export function decodeProjectedGa(s: string | null | undefined): ProjectedGa {
  if (s == null) return { kind: "unset" };
  if (s === "TBD") return { kind: "tbd" };
  if (s.startsWith("Q:")) return { kind: "quarter", value: s.slice(2) };
  if (s.startsWith("W:")) return { kind: "week", value: s.slice(2) };
  return { kind: "date", value: s };
}

export function encodeProjectedGa(pg: ProjectedGa): string | null {
  switch (pg.kind) {
    case "unset":
      return null;
    case "tbd":
      return "TBD";
    case "quarter":
      return `Q:${pg.value}`;
    case "week":
      return `W:${pg.value}`;
    case "date":
      return pg.value;
  }
}

/** Human-readable label for display on cards / detail views. */
export function formatProjectedGa(s: string | null | undefined): string {
  const pg = decodeProjectedGa(s);
  switch (pg.kind) {
    case "unset":
      return "";
    case "tbd":
      return "TBD";
    case "quarter":
      return pg.value;
    case "week": {
      // Turn "2026-W45" into "week of Nov 2, 2026" using ISO-8601 week
      // rules (Monday of the ISO week). Fall back to the raw label if
      // unparseable.
      const m = pg.value.match(/^(\d{4})-W(\d{2})$/);
      if (!m) return pg.value;
      const year = Number(m[1]);
      const week = Number(m[2]);
      const monday = mondayOfIsoWeek(year, week);
      if (!monday) return pg.value;
      return `week of ${monday.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`;
    }
    case "date":
      return new Date(pg.value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
  }
}

/** Sort key for chronological ordering. Coarser kinds (quarter, week) sort
 *  at the START of that period; TBD and unset sort last. */
export function sortKeyProjectedGa(s: string | null | undefined): string {
  const pg = decodeProjectedGa(s);
  switch (pg.kind) {
    case "unset":
      return "￿￿";
    case "tbd":
      return "￿￾";
    case "quarter": {
      // "Q1 2026" → "2026-Q1" for lex-sortable order
      const m = pg.value.match(/^Q([1-4])\s+(\d{4})$/);
      return m ? `${m[2]}-Q${m[1]}` : pg.value;
    }
    case "week":
      return pg.value; // already ISO-week sortable
    case "date":
      return pg.value;
  }
}

/** ISO-8601 Monday of a given (year, week). Returns null on invalid input. */
function mondayOfIsoWeek(year: number, week: number): Date | null {
  if (!Number.isFinite(year) || !Number.isFinite(week)) return null;
  // Jan 4 is always in ISO week 1. Find the Monday of that week, then add
  // (week - 1) * 7 days.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7; // 1..7, Monday=1, Sunday=7
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));
  const target = new Date(week1Monday);
  target.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return target;
}
